import React, { useState, useEffect } from 'react';
import Packages from '../screens/billing/Packages';
import { connect } from 'react-redux';
import { getBillingPackages, getKountDetails, postVisitLogs } from '../actions/billingActions';
import { clearErrors } from '../actions/commonActions';
import KountLogo from '../atoms/billing/KountLogo';
import queryString from 'query-string';
import { refreshProfile } from '../actions/profileActions';
import Loading from '../atoms/Loading';
import { Location, NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import TrackLoadingToMixpanel from '../components/common/TrackLoadingToMixPanel';
import { postQuickOffer } from '../actions/offerActions';
import QuickOfferModalInitial from '../modules/QuickOfferModal/QuickOfferModalInitial';
import { AppDispatch, RootState } from '../store';
import { IPackages, IUserProfileData } from '../types/common';
import { IBillingPackagesContainerProps, IBillingPackageAPIResponse } from './types/BillingPackagesContainer';
import useMediaBreakpoints from '../hooks/useMediaBreakpoints';

const findPackages = (allPackages: IPackages[], identifier: string[]): IPackages | undefined => {
  const result = allPackages.find(item => identifier.includes(item.identifier));

  if (result) {
    return result;
  }
};

const getPackageId = (packageId: string | null, defaultPackage: IPackages): string | null => {
  return packageId || (typeof defaultPackage === 'object' && defaultPackage.identifier) || null;
};

const stepLayout = (
  location: Location,
  setShowPayment: React.Dispatch<React.SetStateAction<boolean>>,
  setPackageId: React.Dispatch<React.SetStateAction<string | null>>,
  setPromoCode: React.Dispatch<React.SetStateAction<string | null>>
): void => {
  const queryStringParsed = queryString.parse(location.search);
  const payment = queryStringParsed.step === 'payment';
  const myPackageId = queryStringParsed.package_id || null;
  const myPromoCode = queryStringParsed.promo_code || null;

  setShowPayment(payment);

  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  setPackageId(myPackageId);

  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  setPromoCode(myPromoCode);
};

const redirectToDashboard = (profile: IUserProfileData, packageId: string | null, navigate: NavigateFunction) => {
  if (profile.profile && profile.profile.data.account_type === 'Attractive' && packageId !== 'idv_last_minute') {
    navigate('/dashboard');
  }
};

const BillingPackagesContainer: React.FC<IBillingPackagesContainerProps> = (
  props: IBillingPackagesContainerProps
): JSX.Element => {
  const { isMobile } = useMediaBreakpoints();
  const [processingPayment, setProcessingPayment] = useState(false);
  const [isStackedVariant, setIsStackedVariant] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [packageId, setPackageId] = useState(null);
  const [promoCode, setPromoCode] = useState(null);
  const [preJoinPromoCode, setPrejoinPromoCode] = useState(null);
  const [isVisitLogged, setIsVisitLogged] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { profile, logPageVisit, paymentToken, errors, packages, kountDetails, postQuickOffer } = props;

  const defaultPackage =
    typeof packages === 'object' &&
    packages.filter((item: { promotional: boolean }) => item.promotional === true).length > 0
      ? findPackages(packages, ['generous_cyber_monday_2017_2', 'generous_pp_special_2'])
      : packages[1];

  const defaultPackageId = defaultPackage && getPackageId(packageId, defaultPackage);

  const queryStringParsed = queryString.parse(location.search);
  const promoCodeFromUrlParam = queryStringParsed.promo || null;

  useEffect(() => {
    props.refreshMyProfile();
    props.getBillingPackages('desc', promoCodeFromUrlParam).then((response: IBillingPackageAPIResponse) => {
      if (response?.meta?.promo?.prejoin_code) {
        setPrejoinPromoCode(response.meta.promo.prejoin_code);
        navigate('/packages');
      }
    });
    props.getKountDetails();
    props.clearErrors();
  }, [promoCodeFromUrlParam]);

  useEffect(() => {
    setVariantLayout();
    stepLayout(location, setShowPayment, setPackageId, setPromoCode);
  }, [location, isMobile]);

  useEffect(() => {
    if (!isVisitLogged) {
      setTimeout(() => {
        logPageVisit('packages', 1);
        setIsVisitLogged(true);
      }, 500);
    }
  }, [isVisitLogged, logPageVisit]);

  useEffect(() => {
    const savedCard = (paymentToken && paymentToken[0]) || null;
    const newCard = document.getElementById('new_card');

    if (processingPayment && errors && typeof errors.data !== 'undefined') {
      setProcessingPayment(false);

      if (savedCard && newCard !== null) {
        newCard.click();
      }
    }
  }, [errors, paymentToken, processingPayment]);

  const setVariantLayout = (): void => {
    const stackedVariant = isMobile;

    setIsStackedVariant(stackedVariant);
  };

  if (!packages) {
    return (
      <div>
        <TrackLoadingToMixpanel page="Packages" />
        <div style={{ textAlign: 'center', marginTop: 100 }}>
          <Loading />
        </div>
      </div>
    );
  }

  if (typeof packages !== 'object') {
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    return false;
  }

  redirectToDashboard(profile, packageId, navigate);

  return (
    <>
      {/* @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389 */}
      <Packages
        packages={packages}
        stackedVariant={isStackedVariant}
        defaultPackageId={defaultPackageId}
        showPayment={showPayment}
        promoCode={promoCode}
        promoCodeFromUrlParam={preJoinPromoCode || promoCodeFromUrlParam}
      />
      <KountLogo details={kountDetails} />
      {profile?.profile?.data?.account_type === 'Generous' && (
        <QuickOfferModalInitial
          profile={profile}
          clearErrors={clearErrors}
          postQuickOffer={postQuickOffer}
          id={'packagesQuickOffer'}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    profile: state.profile,
    packages: state.billing.packages,
    kountDetails: state.billing.kountDetails,
    loading: state.common.isFetching,
    errors: state.common.errors,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    refreshMyProfile: () => {
      dispatch(refreshProfile());
    },
    getBillingPackages: (order = 'desc', promoCode?: string | null | undefined, isPreJoin = false): any => {
      return dispatch(getBillingPackages(order, promoCode, isPreJoin));
    },
    getKountDetails: () => {
      dispatch(getKountDetails());
    },
    clearErrors: () => {
      dispatch(clearErrors());
    },
    logPageVisit: (type: string, step: number) => {
      dispatch(postVisitLogs(type, step));
    },
    postQuickOffer: (formData: { accounts: string[]; price: number }): any => {
      return dispatch(postQuickOffer(formData));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BillingPackagesContainer);
